import React, { useEffect, useState } from 'react';
import { Alert, Button, Form } from 'react-bootstrap';
import AccessibleTable from './AccessibleTable';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { GraduationApi, GraduateStaffApplications, GraduatePageSize, errorMessage } from '../services/GraduationApi';
import { useAuthSession, useCanViewGraduateStaff, useGraduateStaffMode, useCanManageGraduateAdminNotes, useCanEditGraduateApplication, useImpersonation } from './StudentWorkspace';
import { GraduateStaffModeStore } from '../services/GraduateStaffModeStore';
import GraduateApplicationNotesDialog from './GraduateApplicationNotesDialog';
import GraduateApplicationsExportDialog from './GraduateApplicationsExportDialog';

const groups = [
  { type: 0, title: 'Doctoral' },
  { type: 1, title: 'Masters or Specialists' },
  { type: 2, title: 'Certificates' },
] as const;
const columns = [
  { key: 'student', label: 'Student' },
  { key: 'program', label: 'Program' },
  { key: 'graduationTerm', label: 'Graduation term' },
  { key: 'submitted', label: 'Submitted' },
  { key: 'status', label: 'Status' },
  { key: 'approvals', label: 'Faculty approvals' },
] as const;
type GroupType = typeof groups[number]['type'];
type Filter = { identifier: string; page: number; pageSize: GraduatePageSize; sort: typeof columns[number]['key']; direction: 'asc' | 'desc' };
type GroupLoad = { scope: string; request: string; data: GraduateStaffApplications | null; loading: boolean; error: string };

function useGroupApplications(allowed: boolean, scope: string, type: GroupType, filter: Filter, status: number | null, revision: number) {
  const { identifier, page, pageSize, sort, direction } = filter;
  const request = JSON.stringify([identifier, status, page, pageSize, sort, direction, revision]);
  const [load, setLoad] = useState<GroupLoad>({ scope: '', request: '', data: null, loading: true, error: '' });
  useEffect(() => {
    if (!allowed) return;
    const controller = new AbortController();
    setLoad(previous => ({ scope, request, data: previous.scope === scope ? previous.data : null, loading: true, error: '' }));
    GraduationApi.graduateStaffApplications(identifier, status, page, controller.signal, type, sort, direction, pageSize)
      .then(data => { if (!controller.signal.aborted) setLoad({ scope, request, data, loading: false, error: '' }); })
      .catch(error => {
        if (!controller.signal.aborted && !axios.isCancel(error))
          setLoad(previous => ({ ...previous, loading: false, error: errorMessage(error) }));
      });
    return () => controller.abort();
  }, [allowed, scope, type, identifier, status, page, pageSize, sort, direction, revision, request]);
  return { data: allowed && load.scope === scope ? load.data : null,
    loading: allowed && (load.scope !== scope || load.request !== request || load.loading),
    error: load.scope === scope && load.request === request ? load.error : '' };
}

function filterFor(params: URLSearchParams, type: GroupType): Filter {
  const pageValue = Number(params.get(`page${type}`));
  const requestedSort = params.get(`sort${type}`);
  const rows = params.get(`rows${type}`);
  const pageSize = rows === 'all' ? 'all' : rows === '25' ? 25 : 10;
  return {
    identifier: params.get(`search${type}`) ?? params.get('identifier') ?? '',
    page: pageSize !== 'all' && Number.isInteger(pageValue) && pageValue > 0 && pageValue <= 100000 ? pageValue : 1,
    pageSize,
    sort: columns.find(column => column.key === requestedSort)?.key || 'submitted',
    direction: params.get(`direction${type}`) === 'asc' ? 'asc' : 'desc',
  };
}

export default function GraduateApplicationsAdmin() {
  const allowed = useCanViewGraduateStaff();
  const navigate = useNavigate();
  const { kind, canEvaluate, canReview } = useGraduateStaffMode();
  const canManageAdminNotes = useCanManageGraduateAdminNotes();
  const canEditApplication = useCanEditGraduateApplication();
  const impersonation = useImpersonation();
  const account = useAuthSession();
  const [notesApplication, setNotesApplication] = useState<{ id: number; studentName: string } | null>(null);
  const [showExport, setShowExport] = useState(false);
  const [params, setParams] = useSearchParams();
  const statusRaw = params.get('status');
  const status = statusRaw !== null && /^\d+$/.test(statusRaw) ? Number(statusRaw) : null;
  const filters = groups.map(group => filterFor(params, group.type));
  const [inputs, setInputs] = useState<Record<GroupType, string>>({ 0: filters[0].identifier, 1: filters[1].identifier, 2: filters[2].identifier });
  const [validation, setValidation] = useState<Record<GroupType, string>>({ 0: '', 1: '', 2: '' });
  const [revision, setRevision] = useState<Record<GroupType, number>>({ 0: 0, 1: 0, 2: 0 });
  const query = params.toString();
  const scope = JSON.stringify([account?.starId, kind, impersonation?.handle]);
  const doctoral = useGroupApplications(allowed, scope, 0, filters[0], status, revision[0]);
  const masters = useGroupApplications(allowed, scope, 1, filters[1], status, revision[1]);
  const certificates = useGroupApplications(allowed, scope, 2, filters[2], status, revision[2]);
  const loads = [doctoral, masters, certificates];
  const results = loads.map(load => load.data).filter((result): result is GraduateStaffApplications => result !== null);
  const loading = loads.some(load => load.loading);
  const refresh = (type?: GroupType) => setRevision(previous => type === undefined
    ? { 0: previous[0] + 1, 1: previous[1] + 1, 2: previous[2] + 1 }
    : { ...previous, [type]: previous[type] + 1 });

  useEffect(() => { setNotesApplication(null); setShowExport(false); }, [kind, impersonation?.handle, canManageAdminNotes]);

  useEffect(() => {
    setInputs({ 0: filterFor(params, 0).identifier, 1: filterFor(params, 1).identifier, 2: filterFor(params, 2).identifier });
    setValidation({ 0: '', 1: '', 2: '' });
  }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!allowed) return <Navigate to="/" replace />;

  const chooseStatus = (nextStatus: number | null) => {
    const next = new URLSearchParams();
    if (nextStatus !== null) next.set('status', String(nextStatus));
    groups.forEach((group,index) => {
      if (filters[index].pageSize !== 10) next.set(`rows${group.type}`, String(filters[index].pageSize));
    });
    setParams(next);
  };
  const updateTable = (type: GroupType, filter: Filter) => {
    const next = new URLSearchParams(params);
    // Keep a legacy shared search when a bookmarked URL is changed in one table.
    if (next.has('identifier')) {
      groups.forEach(group => {
        if (!next.has(`search${group.type}`)) next.set(`search${group.type}`, next.get('identifier')!);
      });
      next.delete('identifier');
    }
    if (filter.identifier) next.set(`search${type}`, filter.identifier);
    else next.delete(`search${type}`);
    if (filter.page > 1) next.set(`page${type}`, String(filter.page));
    else next.delete(`page${type}`);
    if (filter.pageSize !== 10) next.set(`rows${type}`, String(filter.pageSize));
    else next.delete(`rows${type}`);
    if (filter.sort !== 'submitted' || filter.direction !== 'desc') {
      next.set(`sort${type}`, filter.sort); next.set(`direction${type}`, filter.direction);
    } else { next.delete(`sort${type}`); next.delete(`direction${type}`); }
    setParams(next);
  };
  const search = (event: React.FormEvent, type: GroupType, filter: Filter) => {
    event.preventDefault();
    const value = inputs[type].trim().toLowerCase();
    if (value.length > 50 || Array.from(value).some(c => c.charCodeAt(0) < 32 || '%_[]'.includes(c))) {
      setValidation(previous => ({ ...previous, [type]: 'Enter a search term without wildcard characters.' }));
      return;
    }
    setValidation(previous => ({ ...previous, [type]: '' }));
    updateTable(type, { ...filter, identifier: value, page: 1 });
  };
  const statuses = results[0]?.statuses || [];
  return <section className="admin-undergraduate" aria-labelledby="graduate-staff-heading">
    <header className="mb-4"><p className="admin-eyebrow">{kind === 'GraduateFaculty' ? 'Graduate faculty' : 'Graduate evaluation'}</p>
      <h1 id="graduate-staff-heading">Graduate Applications</h1>
      <p className="text-secondary">{kind === 'GraduateFaculty' ? 'Review applications assigned to you for faculty approval.' :
        'Browse graduate applications by status, degree type, or student.'}</p></header>
    {(canEvaluate || canReview) && <div className="mb-4" role="group" aria-label="Graduate staff view">
      {canEvaluate && <Button variant={kind === 'GraduateEvaluator' ? 'primary' : 'outline-primary'} aria-pressed={kind === 'GraduateEvaluator'}
        onClick={() => GraduateStaffModeStore.set('GraduateEvaluator')}>Graduate evaluator</Button>}{' '}
      {canReview && <Button variant={kind === 'GraduateFaculty' ? 'primary' : 'outline-primary'} aria-pressed={kind === 'GraduateFaculty'}
        onClick={() => GraduateStaffModeStore.set('GraduateFaculty')}>My faculty approvals</Button>}
    </div>}
    {loading && !results.length && <p role="status">Loading graduate applications…</p>}
    {results.length > 0 && <section aria-labelledby="graduate-status-heading" className="section-panel mb-4">
      <h2 id="graduate-status-heading" className="h4">Current review queue</h2>
      <div className="admin-status-grid">
        <button className="admin-status-card" aria-pressed={status === null && !filters.some(filter => filter.identifier)} onClick={() => chooseStatus(null)}>
          <span>All current applications</span><strong>{statuses.reduce((sum, item) => sum + item.count, 0).toLocaleString()}</strong><span className="admin-card-action">View all →</span>
        </button>
        {statuses.map(item => <button key={item.statusId} className="admin-status-card"
          aria-pressed={status === item.statusId && !filters.some(filter => filter.identifier)} onClick={() => chooseStatus(item.statusId)}>
          <span>{item.status}</span><strong>{item.count.toLocaleString()}</strong><span className="admin-card-action">View applications →</span>
        </button>)}
      </div>
    </section>}
    <div aria-busy={loading}>
      {results.length > 0 &&
      <div className="d-flex flex-wrap justify-content-between gap-2 mb-3">
        <div><h2 className="h4">{status !== null ? statuses.find(item => item.statusId === status)?.status || 'Filtered applications' : 'All current applications'}</h2>
          <p className="mb-0 text-secondary" role="status">{results.reduce((total, result) => total + result.total, 0).toLocaleString()} applications{results.length < groups.length ? ' in loaded groups' : kind === 'GraduateEvaluator' ? ' across all three groups' : ''}</p></div>
        <div className="d-flex align-items-center gap-2">
          {canManageAdminNotes && kind === 'GraduateEvaluator' && <Button size="sm" onClick={() => setShowExport(true)}>Export to Excel</Button>}
          <Button variant="outline-primary" size="sm" onClick={() => refresh()}>Refresh</Button>
        </div>
      </div>}
      {groups.map((group, index) => {
        const filter = filters[index];
        const load = loads[index];
        const result = load.data;
        if (kind === 'GraduateFaculty' && result?.total === 0 && !load.loading && !load.error) return null;
        return <section key={group.type} className="section-panel mb-4" aria-busy={load.loading} aria-labelledby={`graduate-group-${group.type}`}>
          <div className="d-flex flex-wrap justify-content-between align-items-baseline gap-2">
            <h3 id={`graduate-group-${group.type}`} className="h5">{group.title}</h3>
            <div className="d-flex flex-wrap align-items-center gap-3">
              {result && <span className="text-secondary">{result.total.toLocaleString()} {result.total === 1 ? 'application' : 'applications'}</span>}
              <Form.Group controlId={`graduate-rows-${group.type}`} className="d-flex align-items-center gap-2">
                <Form.Label className="mb-0 text-nowrap">Rows per page</Form.Label>
                <Form.Select size="sm" aria-label={`${group.title} rows per page`} value={filter.pageSize}
                  onChange={event => updateTable(group.type, { ...filter, page: 1,
                    pageSize: event.target.value === 'all' ? 'all' : event.target.value === '25' ? 25 : 10 })}>
                  <option value="10">10</option><option value="25">25</option><option value="all">All</option>
                </Form.Select>
              </Form.Group>
            </div>
          </div>
          <Form onSubmit={event => search(event, group.type, filter)} noValidate className="mb-3">
            <Form.Label htmlFor={`graduate-search-${group.type}`}>Search {group.title} by student, ID, program, term, or status</Form.Label>
            <div className="d-flex flex-wrap gap-2 align-items-start"><div className="admin-search-input">
              <Form.Control id={`graduate-search-${group.type}`} value={inputs[group.type]} autoComplete="off" spellCheck={false}
                onChange={event => { setInputs(previous => ({ ...previous, [group.type]: event.target.value }));
                  setValidation(previous => ({ ...previous, [group.type]: '' })); }} isInvalid={Boolean(validation[group.type])} />
              {validation[group.type] && <Form.Control.Feedback type="invalid" role="alert">{validation[group.type]}</Form.Control.Feedback>}
            </div><Button type="submit">Search {group.title}</Button>
              <Button variant="outline-secondary" type="button" onClick={() => {
                setInputs(previous => ({ ...previous, [group.type]: '' }));
                updateTable(group.type, { ...filter, identifier: '', page: 1 });
              }}>Clear</Button></div>
            <Form.Text>Search includes older and archived applications within your access.</Form.Text>
          </Form>
          {load.loading && <p role="status">Loading {group.title.toLowerCase()} applications…</p>}
          {load.error && <Alert variant="danger" role="alert">{group.title}: {load.error} <Button variant="outline-danger" size="sm" onClick={() => refresh(group.type)}>Try again</Button></Alert>}
          {result && (result.applications.length === 0 ? <p>No {group.title.toLowerCase()} applications in this view.</p> :
            <AccessibleTable scrollLabel={`${group.title} graduate application results`} striped hover className="align-middle">
              <caption className="visually-hidden">{group.title} graduate applications</caption>
              <thead><tr>{columns.map(column => <th key={column.key} scope="col"
                aria-sort={filter.sort === column.key ? filter.direction === 'asc' ? 'ascending' : 'descending' : undefined}>
                <button type="button" className="admin-sort-button" onClick={() => updateTable(group.type, {
                  ...filter, page: 1, sort: column.key,
                  direction: filter.sort === column.key && filter.direction === 'asc' ? 'desc' : 'asc',
                })} aria-label={`${group.title} ${column.label}: sort ${filter.sort === column.key && filter.direction === 'asc' ? 'descending' : 'ascending'}`}>
                  {column.label} <span aria-hidden="true">{filter.sort === column.key ? filter.direction === 'asc' ? '↑' : '↓' : '↕'}</span>
                </button>
              </th>)}<th scope="col"><span className="visually-hidden">Action</span></th></tr></thead>
              <tbody>{result.applications.map(app => <tr key={app.applicationId}><td><strong>{app.studentName}</strong>
                <div className="small text-secondary">{app.techId ? `Tech ID #${app.techId}` : 'Tech ID not available'}</div></td>
                <td>{app.program || 'Not provided'}</td><td>{app.graduationTerm || 'Not specified'}</td>
                <td>{new Date(app.submissionDate).toLocaleDateString()}</td>
                <td>{kind === 'GraduateFaculty' && app.ownReviewApproved && app.status.trim().toLowerCase() !== 'need to meet' ? 'Approved' : app.status}{app.archived ? ' (Archived)' : ''}</td>
                <td>{app.signaturesApproved} of {app.signaturesRequested}</td>
                <td><div className="d-flex flex-wrap align-items-center gap-2">
                  <Button size="sm" variant="outline-primary"
                    onClick={() => navigate(`/admin/graduate/${app.applicationId}?list=${encodeURIComponent(params.toString())}`)}
                    aria-label={`View graduate application ${app.applicationId} for ${app.studentName}`}>View</Button>
                  {canEditApplication && !app.archived && <Button size="sm" variant="outline-primary"
                    onClick={() => navigate(`/admin/graduate/${app.applicationId}/edit?list=${encodeURIComponent(params.toString())}`)}
                    aria-label={`Edit graduate application ${app.applicationId} for ${app.studentName}`}>Edit</Button>}
                  {canManageAdminNotes && app.hasNotes && <Button size="sm" variant="outline-primary"
                    aria-label={`View or add notes for graduate application ${app.applicationId} for ${app.studentName}`}
                    onClick={() => setNotesApplication({ id: app.applicationId, studentName: app.studentName })}>Notes</Button>}
                </div></td></tr>)}</tbody>
            </AccessibleTable>)}
          {result && filter.pageSize !== 'all' && (result.total > result.pageSize || filter.page > 1) && <nav aria-label={`${group.title} application result pages`}
            className="d-flex align-items-center justify-content-end gap-3">
            <Button variant="outline-secondary" disabled={filter.page === 1} onClick={() => updateTable(group.type, { ...filter, page: filter.page - 1 })}>Previous</Button>
            <span>Page {filter.page} of {Math.max(1, Math.ceil(result.total / result.pageSize))}</span>
            <Button variant="outline-secondary" disabled={filter.page * result.pageSize >= result.total}
              onClick={() => updateTable(group.type, { ...filter, page: filter.page + 1 })}>Next</Button>
          </nav>}
        </section>;
      })}
    </div>
    {canManageAdminNotes && notesApplication && <GraduateApplicationNotesDialog key={notesApplication.id}
      applicationId={notesApplication.id} studentName={notesApplication.studentName} onClose={() => setNotesApplication(null)} />}
    {canManageAdminNotes && kind === 'GraduateEvaluator' && showExport && <GraduateApplicationsExportDialog
      categories={groups.map((group, index) => ({ applicationType: group.type, label: group.title,
        identifier: filters[index].identifier, sort: filters[index].sort, direction: filters[index].direction }))}
      statusId={status} onClose={() => setShowExport(false)} />}
  </section>;
}
